import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { ModalSize, OptionsHelper, QUERIES } from '../../../../../../_metronic/helpers'
import { CustomSelect, IOption, useModal } from '../../../../../components'
import { isVinculoMedicoAtivo } from 'app/modules/auth/core/vinculos'
import { BlocoAgenda, EscalaCalendario } from '../../components/EscalaCalendario'
import { EscalaAgendaDiaModal } from '../../components/EscalaAgendaDiaModal'
import { getAgendaPessoa, getMeusVinculos } from '../../core/_requests'
import { estadoDaAgendaPessoal, formatFaixaHorario } from '../../core/escalaHelpers'
import { ParamsMinhaEscala, useQueryRequest } from '../core/QueryRequestProvider'

export const MinhaEscalaCalendario = () => {
  const { state, updateState } = useQueryRequest<ParamsMinhaEscala>()
  const { showModal } = useModal()

  const params = state.params ?? {}
  const { inicio, termino, empresa, especialidade } = params

  const { data: vinculos } = useQuery(
    [QUERIES.ESCALAS_PESSOAL_AGENDA, 'vinculos'],
    getMeusVinculos,
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  )

  const opcoesEmpresas: IOption[] = useMemo(() => {
    const vistos = new Set<number>()
    return (vinculos ?? [])
      .filter(isVinculoMedicoAtivo)
      .filter((vinculo) => {
        const id = vinculo.empresa?.id
        if (id == null || vistos.has(id)) return false
        vistos.add(id)
        return true
      })
      .map((vinculo) => ({
        value: vinculo.empresa!.id,
        label: vinculo.empresa!.nomeFantasia ?? vinculo.empresa!.nome ?? '',
      }))
  }, [vinculos])

  const {
    data: agenda,
    isFetching,
    refetch,
  } = useQuery(
    [QUERIES.ESCALAS_PESSOAL_AGENDA, inicio, termino, empresa, especialidade],
    () => getAgendaPessoa(inicio!, termino!, empresa, especialidade),
    {
      enabled: Boolean(inicio && termino),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const blocos: BlocoAgenda[] = useMemo(() => {
    if (!agenda) return []

    // A resposta é `Map<LocalDate, List<EscalaAgendaViewPessoa>>`; a chave é a
    // própria data, então basta achatar os valores.
    return Object.values(agenda)
      .flat()
      .map((dia) => ({
        id: String(dia.id),
        data: dia.data,
        horarioInicio: dia.horarioInicio,
        horarioTermino: dia.horarioTermino,
        estado: estadoDaAgendaPessoal(dia),
        titulo: dia.especialidades?.map((item) => item.nome).join(', ') || undefined,
        detalhe:
          dia.empresas?.map((item) => item.nomeFantasia ?? item.nome).join(', ') ||
          (dia.indisponivel ? 'Todas as suas instituições' : undefined),
        // No eixo pessoal tudo é do próprio dono, logo tudo é gerenciável.
        gerenciavel: true,
      }))
  }, [agenda])

  const abrirDia = async (bloco: BlocoAgenda) => {
    const dia = Object.values(agenda ?? {})
      .flat()
      .find((item) => String(item.id) === bloco.id)
    if (!dia) return

    showModal({
      title: `Marcação de ${dia.dataFormatada ?? dia.data} — ${formatFaixaHorario(
        dia.horarioInicio,
        dia.horarioTermino
      )}`,
      size: ModalSize.Large,
      content: <EscalaAgendaDiaModal eixo='pessoal' agenda={dia} onSuccess={refetch} />,
    })
  }

  const atualizaParams = (novos: Partial<ParamsMinhaEscala>) =>
    updateState({ params: { ...params, ...novos } })

  return (
    <div className='card-body pt-0'>
      <div className='row mb-6'>
        {/* `label` nativo envolvendo o select: o `CustomSelect` não expõe
            `aria-label`, e a associação implícita é o que dá nome acessível ao
            combobox do react-select (WCAG 2.2 AA). */}
        <div className='col-md-4'>
          <label className='d-block'>
            <span className='fs-6 fw-bolder mb-2 d-block'>Instituição</span>
            <CustomSelect
              name='empresa'
              placeholder='Todas as instituições'
              options={opcoesEmpresas}
              isClearable
              value={opcoesEmpresas.find((opcao) => opcao.value === empresa)}
              onChange={(opcao: IOption | null) =>
                atualizaParams({ empresa: opcao ? Number(opcao.value) : undefined })
              }
            />
          </label>
        </div>
        <div className='col-md-4'>
          <label className='d-block'>
            <span className='fs-6 fw-bolder mb-2 d-block'>Especialidade</span>
            <CustomSelect
              name='especialidade'
              placeholder='Todas as especialidades'
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              isClearable
              onChange={(opcao: IOption | null) =>
                atualizaParams({ especialidade: opcao ? Number(opcao.value) : undefined })
              }
            />
          </label>
        </div>
      </div>

      <EscalaCalendario
        blocos={blocos}
        isLoading={isFetching}
        estadosNaLegenda={['disponivel', 'indisponivel', 'indisponibilidade-global']}
        onVisibleRangeChange={({ inicio: de, termino: ate }) =>
          atualizaParams({ inicio: de, termino: ate })
        }
        onSelecionar={abrirDia}
      />
    </div>
  )
}
